// Texts of the checkout's delivery step. Keys start with "delivery.".
import { NoTranslate } from "../../../NewComponents/TranslationSafe";

const en = {
  // Delivery speed
  "delivery.whenTitle": "When do you need it?",
  "delivery.deliveredOn": "Delivered on your order page.",
  "delivery.estimatedFor": "Estimated times for {pages}.",
  "delivery.standardText": "Our regular turnaround.",
  "delivery.expeditedText": "Your order is prioritized and delivered in half the time.",
  "delivery.expeditedPlusText": "Our fastest turnaround.",
  "delivery.included": "Included",
  "delivery.perPage": "/page",
  "delivery.freeLeft": ({ n }) => `(${n} free orders left)`,
  // Followed by the date, in its own element.
  "delivery.readyBy": "Ready by ",
  "delivery.plusClosedAndScript": "Available from 8 AM to 5 PM ET, and not for non-Latin languages.",
  "delivery.plusClosed": "Available from 8 AM to 5 PM ET.",
  "delivery.plusScript": "Not available for non-Latin languages.",

  // Extras
  "delivery.extrasTitle": "Extras",
  "delivery.extrasCertifiedOnly": "Notarization and mailed hard copies are only for certified translations.",
  "delivery.notarizationText": "Stamp and signature authenticating the translation certification. Valid in all 50 states.",
  "delivery.hardCopyText": "Original translation with wet ink signature(s) shipped via USPS Priority or Express mail.",
  // Hard copy price before the country and the shipping method are chosen: the cheapest one.
  "delivery.hardCopyFrom": "from",
  // What a team's folded "More options" holds on this step.
  "delivery.moreHint": "Notarization, mailed hard copy",

  // Mailing address
  "delivery.myAddress": "My Address",
  "delivery.newAddress": "New Address",
  "delivery.primary": "(primary)",
  "delivery.edit": "Edit",
  "delivery.delete": "Delete",
  "delivery.addressName": "Address Name",
  "delivery.country": "Select Country",
  "delivery.state": "Select State",
  "delivery.city": "Select City",
  "delivery.zipCode": "ZIP code",
  "delivery.street": "Street/Address",
  "delivery.apartment": "Apartment, suite, etc.",
  "delivery.method": "Delivery",
  // Followed by the price, in its own element.
  "delivery.requireSignature": "Require signature for delivery ",
  "delivery.saveAddress": "Save address",
  "delivery.deleteTitle": "Delete Address",
  "delivery.deleteButton": "Delete Address",
  "delivery.deleting": "Deleting...",
  "delivery.deleteBack": "Back to Shipping Addresses",
  "delivery.deleteConfirm": "Are you sure you want to delete this address?",
  "delivery.selectAddress": "Please select an address or add a new address.",
  "delivery.addressDeleted": "Address deleted successfully.",
  "delivery.addressUpdated": "Address updated successfully.",

  // Business orders: the client
  "delivery.clientTitle": "Your Client",
  "delivery.clientText": "If submitted, the client field will be included in emails, in translations, and in your invoice.",
  "delivery.clientFirstName": "Client First Name",
  "delivery.clientLastName": "Client Last Name",
  "delivery.caseNumber": "Case No.",
  // Under the client's name when it was taken from the uploaded files' names.
  "delivery.clientFromFile": "From your file name. Please check it.",
  "delivery.similarClients": "We found similar clients in your account. Would you like to select one of them for this order?",
  "delivery.clientOrders": ({ n }) => `(${n} orders)`,

  // Summary
  "delivery.discount": ({ percent }) => <>Discount (<NoTranslate>{percent}%</NoTranslate>)</>,

  // Phones: why the button is disabled
  "delivery.forTitle": "Who is asking for this translation?",
  "delivery.forOptional": "optional",
  "delivery.forText": "Pick all that apply (your attorney and a university, for example). We make sure the translation meets their requirements.",
  "delivery.forAttorney": "My attorney or law firm",
  "delivery.forUniversity": "A university or school",
  "delivery.forUscis": "USCIS",
  "delivery.forCourt": "A court",
  "delivery.forDmv": "DMV",
  "delivery.forOther": "Someone else",
  "delivery.forAttorneyName": "Attorney or law firm",
  "delivery.forUniversityName": "University or school",
  "delivery.forCourtName": "Which court? (optional)",
  "delivery.forDmvState": "Which state?",
  "delivery.forDmvHint": "Some states call it MVD, RMV or BMV. They all count as the DMV.",
  "delivery.forDmvPick": "Choose a state",
  "delivery.forOtherName": "Who is it? (an employer, a bank, a consulate...)",
  "delivery.forPickList": "Suggestions",
  "delivery.forPickSuggested": "From the page you were reading",
  "delivery.forPickHint": "Type the name or its abbreviation (UCLA, WES). Can't find it? Type the full name.",
  "delivery.forPickLoading": "Loading the list...",
  "delivery.forPickTyped": ({ name }) => <>Can't find it? Use <NoTranslate>“{name}”</NoTranslate></>,
  "delivery.forPickEvaluator": "Credential evaluator",
  "delivery.forPickDistrict": "School district",
};

const es = {
  "delivery.whenTitle": "¿Para cuándo la necesita?",
  "delivery.deliveredOn": "La recibirá en la página de su pedido.",
  "delivery.estimatedFor": "Plazos estimados para {pages}.",
  "delivery.standardText": "Nuestro plazo habitual.",
  "delivery.expeditedText": "Su pedido tiene prioridad y se entrega en la mitad del tiempo.",
  "delivery.expeditedPlusText": "Nuestro plazo más rápido.",
  "delivery.included": "Incluido",
  "delivery.perPage": "/página",
  "delivery.freeLeft": ({ n }) => (Number(n) === 1 ? "(le queda 1 pedido gratis)" : `(le quedan ${n} pedidos gratis)`),
  "delivery.readyBy": "Lista para el ",
  "delivery.plusClosedAndScript": "Disponible de 8:00 a 17:00, hora del Este (ET), y solo para idiomas que usan el alfabeto latino.",
  "delivery.plusClosed": "Disponible de 8:00 a 17:00, hora del Este (ET).",
  "delivery.plusScript": "No disponible para idiomas que no usan el alfabeto latino.",

  "delivery.extrasTitle": "Extras",
  "delivery.extrasCertifiedOnly": "La notarización y la copia impresa por correo solo están disponibles para traducciones certificadas.",
  "delivery.notarizationText": "Sello y firma de un notario que autentican la certificación de la traducción. Válida en los 50 estados.",
  "delivery.hardCopyText": "Traducción original con firma(s) en tinta, enviada por USPS Priority o Express.",
  "delivery.hardCopyFrom": "desde",
  "delivery.moreHint": "Notarización, copia impresa por correo",

  "delivery.myAddress": "Mis direcciones",
  "delivery.newAddress": "Nueva dirección",
  "delivery.primary": "(principal)",
  "delivery.edit": "Editar",
  "delivery.delete": "Eliminar",
  "delivery.addressName": "Nombre de la dirección",
  "delivery.country": "País",
  "delivery.state": "Estado / provincia",
  "delivery.city": "Ciudad",
  "delivery.zipCode": "Código postal",
  "delivery.street": "Calle y número",
  "delivery.apartment": "Apartamento, oficina, etc.",
  "delivery.method": "Envío",
  "delivery.requireSignature": "Requerir firma al entregar ",
  "delivery.saveAddress": "Guardar dirección",
  "delivery.deleteTitle": "Eliminar dirección",
  "delivery.deleteButton": "Eliminar dirección",
  "delivery.deleting": "Eliminando...",
  "delivery.deleteBack": "Volver a las direcciones de envío",
  "delivery.deleteConfirm": "¿Seguro que desea eliminar esta dirección?",
  "delivery.selectAddress": "Elija una de sus direcciones o agregue una nueva.",
  "delivery.addressDeleted": "La dirección se eliminó correctamente.",
  "delivery.addressUpdated": "La dirección se actualizó correctamente.",

  "delivery.clientTitle": "Su cliente",
  "delivery.clientText": "Si lo completa, el nombre del cliente aparecerá en los correos, en las traducciones y en su factura.",
  "delivery.clientFirstName": "Nombre del cliente",
  "delivery.clientLastName": "Apellido del cliente",
  "delivery.caseNumber": "N.º de caso",
  "delivery.clientFromFile": "Tomado del nombre de su archivo. Revíselo, por favor.",
  "delivery.similarClients": "Encontramos clientes parecidos en su cuenta. ¿Desea elegir uno de ellos para este pedido?",
  "delivery.clientOrders": ({ n }) => `(${n} ${Number(n) === 1 ? "pedido" : "pedidos"})`,

  "delivery.discount": ({ percent }) => <>Descuento (<NoTranslate>{percent}%</NoTranslate>)</>,

  "delivery.forTitle": "¿Quién le pide esta traducción?",
  "delivery.forOptional": "opcional",
  "delivery.forText": "Elija todas las que correspondan (por ejemplo, su abogado y una universidad). Nos aseguramos de que la traducción cumpla con lo que piden.",
  "delivery.forAttorney": "Mi abogado o estudio jurídico",
  "delivery.forUniversity": "Una universidad o escuela",
  "delivery.forUscis": "USCIS",
  "delivery.forCourt": "Un juzgado",
  "delivery.forDmv": "DMV",
  "delivery.forOther": "Otro",
  "delivery.forAttorneyName": "Abogado o estudio jurídico",
  "delivery.forUniversityName": "Universidad o escuela",
  "delivery.forCourtName": "¿Qué juzgado? (opcional)",
  "delivery.forDmvState": "¿En qué estado?",
  "delivery.forDmvHint": "En algunos estados se llama MVD, RMV o BMV. Todos cuentan como DMV.",
  "delivery.forDmvPick": "Elija un estado",
  "delivery.forOtherName": "¿Quién es? (un empleador, un banco, un consulado...)",
  "delivery.forPickList": "Sugerencias",
  "delivery.forPickSuggested": "De la página que estaba leyendo",
  "delivery.forPickHint": "Escriba el nombre o la sigla (UCLA, WES). ¿No la encuentra? Escriba el nombre completo.",
  "delivery.forPickLoading": "Cargando la lista...",
  "delivery.forPickTyped": ({ name }) => <>¿No la encuentra? Usar <NoTranslate>«{name}»</NoTranslate></>,
  "delivery.forPickEvaluator": "Evaluadora de credenciales",
  "delivery.forPickDistrict": "Distrito escolar",
};

const pt = {
  "delivery.whenTitle": "Para quando você precisa?",
  "delivery.deliveredOn": "Entregue na página do seu pedido.",
  "delivery.estimatedFor": "Prazos estimados para {pages}.",
  "delivery.standardText": "Nosso prazo normal.",
  "delivery.expeditedText": "Seu pedido tem prioridade e é entregue na metade do tempo.",
  "delivery.expeditedPlusText": "Nosso prazo mais rápido.",
  "delivery.included": "Incluído",
  "delivery.perPage": "/página",
  "delivery.freeLeft": ({ n }) => (Number(n) === 1 ? "(resta 1 pedido grátis)" : `(restam ${n} pedidos grátis)`),
  "delivery.readyBy": "Pronta até ",
  "delivery.plusClosedAndScript": "Disponível das 8h às 17h, horário do Leste dos EUA (ET), e somente para idiomas que usam o alfabeto latino.",
  "delivery.plusClosed": "Disponível das 8h às 17h, horário do Leste dos EUA (ET).",
  "delivery.plusScript": "Não disponível para idiomas que não usam o alfabeto latino.",

  "delivery.extrasTitle": "Extras",
  "delivery.extrasCertifiedOnly": "A notarização e a cópia impressa pelo correio estão disponíveis apenas para traduções certificadas.",
  "delivery.notarizationText": "Carimbo e assinatura de um notário que autenticam a certificação da tradução. Válida nos 50 estados.",
  "delivery.hardCopyText": "Tradução original com assinatura(s) a tinta, enviada por USPS Priority ou Express.",
  "delivery.hardCopyFrom": "a partir de",
  "delivery.moreHint": "Notarização, cópia impressa pelo correio",

  "delivery.myAddress": "Meus endereços",
  "delivery.newAddress": "Novo endereço",
  "delivery.primary": "(principal)",
  "delivery.edit": "Editar",
  "delivery.delete": "Excluir",
  "delivery.addressName": "Nome do endereço",
  "delivery.country": "País",
  "delivery.state": "Estado / província",
  "delivery.city": "Cidade",
  "delivery.zipCode": "CEP / código postal",
  "delivery.street": "Rua e número",
  "delivery.apartment": "Apartamento, sala etc.",
  "delivery.method": "Envio",
  "delivery.requireSignature": "Exigir assinatura na entrega ",
  "delivery.saveAddress": "Salvar endereço",
  "delivery.deleteTitle": "Excluir endereço",
  "delivery.deleteButton": "Excluir endereço",
  "delivery.deleting": "Excluindo...",
  "delivery.deleteBack": "Voltar aos endereços de envio",
  "delivery.deleteConfirm": "Tem certeza de que deseja excluir este endereço?",
  "delivery.selectAddress": "Escolha um dos seus endereços ou adicione um novo.",
  "delivery.addressDeleted": "O endereço foi excluído.",
  "delivery.addressUpdated": "O endereço foi atualizado.",

  "delivery.clientTitle": "Seu cliente",
  "delivery.clientText": "Se você preencher, o nome do cliente aparecerá nos e-mails, nas traduções e na sua fatura.",
  "delivery.clientFirstName": "Nome do cliente",
  "delivery.clientLastName": "Sobrenome do cliente",
  "delivery.caseNumber": "N.º do caso",
  "delivery.clientFromFile": "Vem do nome do seu arquivo. Confira, por favor.",
  "delivery.similarClients": "Encontramos clientes parecidos na sua conta. Deseja escolher um deles para este pedido?",
  "delivery.clientOrders": ({ n }) => `(${n} ${Number(n) === 1 ? "pedido" : "pedidos"})`,

  "delivery.discount": ({ percent }) => <>Desconto (<NoTranslate>{percent}%</NoTranslate>)</>,

  "delivery.forTitle": "Quem está pedindo esta tradução?",
  "delivery.forOptional": "opcional",
  "delivery.forText": "Escolha todas as opções que se aplicam (por exemplo, seu advogado e uma universidade). Nós garantimos que a tradução atenda ao que eles pedem.",
  "delivery.forAttorney": "Meu advogado ou escritório de advocacia",
  "delivery.forUniversity": "Uma universidade ou escola",
  "delivery.forUscis": "USCIS",
  "delivery.forCourt": "Um tribunal",
  "delivery.forDmv": "DMV",
  "delivery.forOther": "Outro",
  "delivery.forAttorneyName": "Advogado ou escritório de advocacia",
  "delivery.forUniversityName": "Universidade ou escola",
  "delivery.forCourtName": "Qual tribunal? (opcional)",
  "delivery.forDmvState": "Em qual estado?",
  "delivery.forDmvHint": "Em alguns estados se chama MVD, RMV ou BMV. Todos contam como DMV.",
  "delivery.forDmvPick": "Escolha um estado",
  "delivery.forOtherName": "Quem é? (um empregador, um banco, um consulado...)",
  "delivery.forPickList": "Sugestões",
  "delivery.forPickSuggested": "Da página que você estava lendo",
  "delivery.forPickHint": "Digite o nome ou a sigla (UCLA, WES). Não encontrou? Digite o nome completo.",
  "delivery.forPickLoading": "Carregando a lista...",
  "delivery.forPickTyped": ({ name }) => <>Não encontrou? Usar <NoTranslate>“{name}”</NoTranslate></>,
  "delivery.forPickEvaluator": "Avaliadora de credenciais",
  "delivery.forPickDistrict": "Distrito escolar",
};

const texts = { en, es, pt };

export default texts;
